/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable semi */
/* eslint-disable quotes */
import React, { forwardRef, useState, useEffect, useRef, useCallback, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import { debounce, findIndex, get, every, isEqual, isEmpty, take, uniqBy, uniq, without, map, compact } from 'lodash';
import cn from "../../../../utils/cn";
import Humanize from 'humanize-plus';
import SelectionBubble from "../../SelectionBubble/SelectionBubble";
import Icon from "../../Icon/Icon";
import { ClearSelections, FilterOption, NoMatches, Search, SelectAll } from "./components";
import { buildOptions, countOptions, createFilterId, filterOptions, getInitialFilters, isMultiLevel } from "./utils";
import { ESC_KEY_CODE, UP_ARROW_KEY_CODE, DOWN_ARROW_KEY_CODE } from "../../../../utils/Browser/keyCodes";
import { isKeyPressed } from "../../../../utils/isKeyPressed";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Filter = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    className = '',
    filterKey = '',
    filterSearchOptions = undefined,
    mustHaveOneSelected = false,
    hideSelectAll = false,
    isSingleOptionSelect = false,
    name,
    pluralName = null,
    onFiltersChange,
    asyncSearch = undefined,
    asyncSearchDebounce = 500,
    asyncLoadingText = 'Loading...',
    options,
    optionsRenderLimit = -1,
    noMatchesPlaceholder = 'No results found',
    searchPlaceholder = 'Search',
    uniqIdPrefix = '',
    dataTestId = '',
    disableParentSelect = false,
    styles = {},
    position = 'right'
  } = _ref;
  const [optionsState, setOptionsState] = useState(options);
  const [activeFilters, setActiveFilters] = useState(getInitialFilters(options));
  const [selectedOptions, setSelectedOptionsState] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const activeFiltersRef = useRef(activeFilters);
  const selectedOptionsRef = useRef(selectedOptions);
  const [availableOptions, setAvailableOptions] = useState(optionsState);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [totalOptionsCount, setTotalOptionsCount] = useState(countOptions(options));
  const filterWrapperRef = useRef(null);
  const searchFieldRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const navList = useMemo(() => {
    const list = [];
    parentOptions.forEach((option, pIndex) => {
      list.push({
        isParent: true,
        parentIndex: pIndex,
        option
      });
      if (option.children) {
        option.children.forEach((child, cIndex) => {
          list.push({
            isParent: false,
            parentIndex: pIndex,
            childIndex: cIndex,
            option: child
          });
        });
      }
    });
    return list;
  }, [parentOptions]);
  const optionRefs = useRef([]);
  useEffect(() => {
    optionRefs.current = navList.map(() => /*#__PURE__*/React.createRef());
  }, [navList]);
  const setAvailableAndSelectedOptions = function () {
    let newAvailableOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    let newOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    const canBeSelectedOptions = uniqBy([...selectedOptionsRef.current, ...newOptions], 'value');
    const updatedSelectedOptions = canBeSelectedOptions.filter(opt => !opt.children && activeFiltersRef.current.indexOf(opt.value) >= 0);
    setAvailableOptions(optionsRenderLimit < 0 ? newAvailableOptions : take(newAvailableOptions, optionsRenderLimit));
    setSelectedOptionsState(updatedSelectedOptions);
    setLoading(false);
  };
  const getAvailableOptions = function () {
    let newOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    if (isMultiLevel(newOptions)) {
      return buildOptions({
        options: newOptions,
        selected: activeFiltersRef.current
      });
    }
    return newOptions;
  };
  const asyncFilterOptions = async searchValue => {
    try {
      setLoading(true);
      const fetchedOptions = await asyncSearch(searchValue);
      const available = getAvailableOptions(fetchedOptions);
      setAvailableAndSelectedOptions(available, fetchedOptions);
      setOptionsState(fetchedOptions);
    } catch (error) {
      setAvailableAndSelectedOptions([], []);
    }
  };
  const debouncedAsyncFilterOptions = useCallback(debounce(asyncFilterOptions, asyncSearchDebounce), []);
  const localFilterOptions = () => {
    let optionsToFilter = optionsState;
    if (isMultiLevel(optionsToFilter)) {
      optionsToFilter = buildOptions({
        options: optionsToFilter,
        selected: activeFilters
      });
    }
    let available = optionsToFilter;
    if (!isEmpty(search)) {
      const filteredOptions = filterSearchOptions ? filterSearchOptions(optionsState, search) : filterOptions(optionsState, search);
      available = getAvailableOptions(filteredOptions);
    }
    setAvailableAndSelectedOptions(available, optionsToFilter);
  };
  const onSearchChange = value => {
    if (asyncSearch && search !== value) {
      setSearch(value || '');
      setAvailableOptions([]);
      debouncedAsyncFilterOptions(value);
    } else {
      setSearch(value || '');
      localFilterOptions();
    }
  };
  useEffect(() => {
    const parentsWithChildren = availableOptions.filter(o => get(o, 'hasChildren', false));
    const children = map(parentsWithChildren, 'children', []).flat();
    const options = availableOptions.map(option => {
      if (!children.includes(option)) {
        return option;
      }
      return null;
    });
    setParentOptions(compact(options));
  }, [availableOptions]);
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
    selectedOptionsRef.current = selectedOptions;
  }, [activeFilters, selectedOptions]);
  useEffect(() => {
    const setActiveFiltersAndOptionsCount = () => {
      const initialFilters = getInitialFilters(options);
      setActiveFilters(initialFilters);
      setTotalOptionsCount(countOptions(options));
      localFilterOptions();
      setOptionsState(options);
    };
    if (!open && !isEqual(options, optionsState)) {
      setActiveFiltersAndOptionsCount();
    }
  }, [options]);
  useEffect(() => {
    const onClickOutside = e => {
      if (open) {
        const path = e.path || e.composedPath && e.composedPath();
        const index = findIndex(path, filterWrapperRef.current);
        if (path && index < 0) {
          setOpen(false);
        }
      }
    };
    document.addEventListener('click', onClickOutside);
    setOptionsState(options);
    localFilterOptions();
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, [open]);
  useEffect(() => {
    localFilterOptions();
  }, [activeFilters]);
  useEffect(() => () => {
    debouncedAsyncFilterOptions.cancel();
  }, [debouncedAsyncFilterOptions]);
  useEffect(() => {
    onSearchChange(search);
  }, [search]);
  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1);
    } else {
      setFocusedIndex(0);
    }
  }, [open]);
  const onButtonClick = e => {
    e.preventDefault();
    if (!open) {
      searchFieldRef.current.resetValue();
    }
    setOpen(!open);
  };
  const setSelectedOptions = optionIds => {
    const newSelectedOptions = optionIds.map(id => options.find(o => o.value === id)).filter(o => o !== undefined);
    setSelectedOptionsState(newSelectedOptions);
    setActiveFilters(optionIds);
  };
  useImperativeHandle(ref, () => ({
    props: {
      filterKey
    },
    setSelectedOptions(values) {
      setSelectedOptions(values);
    }
  }), []);
  const onOptionChange = (filter, checked) => {
    const {
      value: optValue
    } = filter;
    const hasChildren = get(filter, 'hasChildren', false);
    const children = get(filter, 'children', []).filter(child => !child.selectionDisabled);
    const childValues = map(children, 'value');
    const parent = options.find(option => option?.children?.includes(filter));
    let newFilters = hasChildren ? without(activeFilters, ...[optValue, ...childValues]) : without(activeFilters, optValue, parent?.value);
    let newSelected = hasChildren ? without(selectedOptions, ...[filter, ...children]) : without(selectedOptions, filter);
    if (isSingleOptionSelect && checked) {
      newFilters = [optValue];
      newSelected = [filter];
    } else if (checked) {
      if (hasChildren) {
        newFilters = uniq(newFilters.concat(optValue, childValues));
        newSelected = uniqBy([...selectedOptions, ...children], 'value');
      }
      newFilters = uniq(newFilters.concat(optValue));
      newSelected = uniqBy(newSelected.concat(filter), 'value');
      const allChildrenSelected = every(parent?.children, child => newFilters.includes(child.value));
      if (parent && allChildrenSelected) {
        newFilters = newFilters.concat(parent?.value);
      }
    }
    setActiveFilters(newFilters);
    setSelectedOptionsState(newSelected);
    onFiltersChange(newFilters, filterKey, {
      setSelectedOptions,
      filterKey: optValue
    });
    localFilterOptions();
  };
  const onSelectAll = () => {
    let optionsToSelect = options;
    if (isMultiLevel(optionsToSelect)) {
      optionsToSelect = buildOptions({
        options: optionsToSelect,
        selected: activeFilters,
        removeDisabled: true
      });
    }
    const newFilters = map(optionsToSelect, o => o.value);
    setActiveFilters(newFilters);
    localFilterOptions();
    onFiltersChange(newFilters, filterKey, setSelectedOptions);
  };
  const onClearAll = () => {
    setActiveFilters([]);
    localFilterOptions();
    onFiltersChange([], filterKey, {
      setSelectedOptions
    });
  };
  const onLabelClick = filter => {
    if (availableOptions.includes(filter)) {
      const parent = options.find(option => option?.children?.includes(filter));
      const optionId = parent ? `${uniqIdPrefix}${parent.value}__${filter.value}` : `${uniqIdPrefix}${filter.value}`;
      const option = document.getElementById(optionId);
      if (option) {
        option.style.scrollMarginTop = '10px';
        option.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };
  const handleDropdownKeyDown = e => {
    if (!parentOptions.length) return;
    if (isKeyPressed(e, "ArrowDown", DOWN_ARROW_KEY_CODE)) {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, navList.length - 1));
    }
    if (isKeyPressed(e, "ArrowUp", UP_ARROW_KEY_CODE)) {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, 0));
    } else if (isKeyPressed(e, "Escape", ESC_KEY_CODE)) {
      e.preventDefault();
      setOpen(false);
    }
  };
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < navList.length && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].current?.focus();
    }
  }, [focusedIndex, navList]);
  const handleButtonKeyDown = e => {
    if (isKeyPressed(e, "ArrowDown", DOWN_ARROW_KEY_CODE) && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };
  const filterClass = cn({
    'max-w-350px min-w-250px relative text-text-blue': true
  }, className);
  const allOptionsSelected = activeFilters.length >= totalOptionsCount;
  const noMatchesFound = availableOptions.length <= 0;
  const filterButtonClass = cn({
    'font-regular-font text-sm text-text-blue block h-10 px-2.125 pl-2.5 py-0 relative text-left select-none w-full border border-solid rounded': true,
    'border-action-blue overflow-hidden': open,
    'bg-dark-fill-blue hover:bg-light-border-blue border-medium-border-blue': !open && !isEmpty(selectedOptions),
    'hover:bg-medium-fill-grey bg-white border-light-border-blue': !open && isEmpty(selectedOptions)
  });
  const dropdownClass = cn({
    'block bg-white absolute bg-gradient-to-t from-white to-light-border-grey rounded border border-solid border-action-blue z-dropdown overflow-hidden mt-2 w-full': open,
    'hidden border-light-border-blue border-t-0 border': !open,
    'right-0': position === 'left',
    'left-0': position === 'right'
  });
  const countClass = cn({
    'inline-block ml-1 font-heavy-font': true
  }, {
    hidden: selectedOptions.length === 0
  });
  const checkIconClass = cn({
    'inline-block mr-2': true
  }, {
    hidden: selectedOptions.length <= 0
  });
  const count = selectedOptions.length;
  const accessibilityLabel = `${Humanize.titleCase(Humanize.pluralize(count || 1, name, pluralName))} (${count}) selected, dropdown`;
  return /*#__PURE__*/_jsxs("div", {
    className: filterClass,
    id: `${createFilterId(name)}-container`,
    ref: filterWrapperRef,
    style: styles.filter,
    children: [/*#__PURE__*/_jsxs("button", {
      role: "button",
      onClick: onButtonClick,
      onKeyDown: handleButtonKeyDown,
      className: filterButtonClass,
      "aria-label": accessibilityLabel,
      "data-testid": dataTestId,
      style: styles.button,
      tabIndex: 0,
      children: [/*#__PURE__*/_jsx("span", {
        className: checkIconClass,
        children: /*#__PURE__*/_jsx(Icon, {
          className: "mb-1",
          color: "text-blue",
          viewBox: "0 0 20 20",
          icon: "Check",
          size: 14
        })
      }), Humanize.titleCase(Humanize.pluralize(count || 1, name, pluralName)), /*#__PURE__*/_jsxs("span", {
        className: countClass,
        children: ["(", /*#__PURE__*/_jsx("span", {
          className: "mx-px",
          children: count
        }), ")"]
      }), /*#__PURE__*/_jsx(Icon, {
        className: "absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none",
        viewBox: "0 0 20 20",
        icon: "ChevronArrow",
        direction: open ? "up" : "down",
        size: 12,
        color: "medium-grey"
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: dropdownClass,
      style: styles.dropdown,
      onKeyDown: handleDropdownKeyDown,
      role: "listbox",
      children: [/*#__PURE__*/_jsx(Search, {
        id: createFilterId(name),
        label: `Search ${name}`,
        onChange: onSearchChange,
        placeholder: searchPlaceholder,
        ref: searchFieldRef
      }), /*#__PURE__*/_jsxs("div", {
        className: "overflow-y-auto select-none max-h-450px relative text-text-blue",
        children: [!isEmpty(selectedOptions) && /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx("div", {
            className: "flex-wrap px-2.5 pt-3",
            children: selectedOptions.map(o => /*#__PURE__*/_jsx(SelectionBubble, {
              label: o.label,
              hideClose: mustHaveOneSelected && count === 1,
              selected: o,
              onClick: onOptionChange,
              onLabelClick: onLabelClick,
              clickable: onLabelClick && availableOptions.includes(o)
            }, `uOpt-${o.value}-selected`))
          }), mustHaveOneSelected ? /*#__PURE__*/_jsx("div", {
            className: "border-b border-light-border-blue"
          }) : /*#__PURE__*/_jsx(ClearSelections, {
            onClick: onClearAll,
            allOptionsSelected: allOptionsSelected
          })]
        }), /*#__PURE__*/_jsxs("div", {
          children: [loading && /*#__PURE__*/_jsx("div", {
            className: "pt-3 pl-2.5 pb-2.5 text-sm box-content block font-regular-font capitalize text-brand-blue tracking-normal",
            children: asyncLoadingText
          }), /*#__PURE__*/_jsx(SelectAll, {
            hide: hideSelectAll || noMatchesFound || allOptionsSelected || optionsRenderLimit > 0,
            onSelectAll: onSelectAll
          }), parentOptions.map((option, pIndex) => {
            const childCount = option.children ? option.children.length : 0;
            const parentNavListIndex = navList.findIndex(row => row.isParent && row.parentIndex === pIndex);
            const childNavListIndexes = [];
            for (let i = 1; i <= childCount; i++) {
              childNavListIndexes.push(parentNavListIndex + i);
            }
            const parentRef = optionRefs.current[parentNavListIndex];
            const childRefs = childNavListIndexes.map(idx => optionRefs.current[idx]);
            return /*#__PURE__*/_jsx(FilterOption, {
              ref: parentRef,
              option: option,
              onChange: onOptionChange,
              activeFilters: activeFilters,
              searchTerm: search,
              disableParentSelect: disableParentSelect,
              uniqIdPrefix: uniqIdPrefix,
              childRefs: childRefs
            }, option.value);
          }), /*#__PURE__*/_jsx(NoMatches, {
            noMatchesFound: !loading && (allOptionsSelected || noMatchesFound),
            placeholder: noMatchesPlaceholder
          })]
        })]
      })]
    })]
  });
});
Filter.propTypes = {
  /** className for reference */
  className: PropTypes.string,
  /** Filter key used when storing selected filter to state. i.e. serviceTypes */
  filterKey: PropTypes.string,
  /** returns an array of filtered searched ServiceType options */
  filterSearchOptions: PropTypes.func,
  /** Don't allow user to remove all options */
  mustHaveOneSelected: PropTypes.bool,
  /** Should hide select all button */
  hideSelectAll: PropTypes.bool,
  /** Only allows one option to be selected at a time */
  isSingleOptionSelect: PropTypes.bool,
  /** Singular name to display on Filter button (i.e. 'Population') */
  name: PropTypes.string.isRequired,
  /** Plural name to display on Filter in case Humanize can't handle it */
  pluralName: PropTypes.string,
  /**
   * @param {array} activeFilters array of selected filters
   * @param {string} key filterKey
   * @public
  */
  onFiltersChange: PropTypes.func.isRequired,
  /**
   * Accepts Promise resolving an array of formatted options
   * @param {string} search
  */
  asyncSearch: PropTypes.func,
  /** Debouce wait for the asyncSearch */
  asyncSearchDebounce: PropTypes.number,
  /** Async loading text displayed while waiting for the promise */
  asyncLoadingText: PropTypes.string,
  /** Array of filter options objects */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    initial: PropTypes.bool,
    selectionDisabled: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      initial: PropTypes.bool,
      selectionDisabled: PropTypes.bool
    }))
  })).isRequired,
  /** Maximum number of options to render in case of long options array (-1 is unlimited) */
  optionsRenderLimit: PropTypes.number,
  /** Placeholder text for when no matches are found */
  noMatchesPlaceholder: PropTypes.string,
  /** Placeholder text for search */
  searchPlaceholder: PropTypes.string,
  /** A prefix to prevent ID conflicts when multiple filters share the same option list */
  uniqIdPrefix: PropTypes.string,
  /** Immutable identifier for testing */
  dataTestId: PropTypes.string,
  /** parent types should not be selectable */
  disableParentSelect: PropTypes.bool,
  /** disable ability to remove selected items */
  disableRemoveSelected: PropTypes.bool,
  /** custom styles */
  styles: PropTypes.object,
  /** position of dropdown container relative to filter button */
  position: PropTypes.oneOf(['right', 'left'])
};
Filter.displayName = 'Filter';
export default Filter;
//# sourceMappingURL=Filter.js.map